/**
 * Store Locator Map
 * Modern Google Maps implementation for store locations
 * ES6 module - uses vanilla JavaScript with modern patterns
 */

import { createReporter } from "@theme/logger";

const reportError = createReporter("StoreLocator");

const showStoreLocatorFallback = () => {
  const mapElement = document.getElementById("map");
  const fallback = document.querySelector("[data-map-fallback]");
  const searchContainer = document.querySelector("[data-map-search]");
  const filter = document.querySelector("[data-store-filter]");
  const sidebar = document.getElementById("sidebar");
  if (mapElement) mapElement.hidden = true;
  if (searchContainer instanceof HTMLElement) searchContainer.hidden = true;
  if (filter instanceof HTMLElement) filter.hidden = true;
  if (sidebar) sidebar.hidden = true;
  if (fallback instanceof HTMLElement) fallback.hidden = false;
};

/**
 * @typedef {object} StoreLocatorConfig
 * @property {string} locationDataUrl
 * @property {string} apiKey
 */

/**
 * @typedef {object} LocationData
 * @property {string} storeName
 * @property {string} storeType
 * @property {string} address
 * @property {string} city
 * @property {string} state
 * @property {string} postalCode
 * @property {string} phone
 * @property {string} fax
 * @property {string | number} mallName
 * @property {number} lat
 * @property {number} lng
 * @property {number} status
 * @property {number} index
 */

const COMPASS = new Set(["N", "S", "E", "W", "NE", "NW", "SE", "SW"]);

/**
 * Title-case ALL-CAPS source strings. Unicode-aware so accented names
 * (Montréal, Trois-Rivières) keep one capital per word. A single letter
 * before an apostrophe is a prefix (O'Mahony, L'Acadie); otherwise the
 * apostrophe is possessive (St. John's).
 * @param {string} value
 */
const toTitleCase = (value) =>
  value.toLowerCase().replace(/[\p{L}\p{N}'’]+/gu, (word) => {
    const upper = word.toUpperCase();
    if (COMPASS.has(upper)) return upper;
    return word.replace(/^(?:\p{L}['’])?\p{L}/u, (lead) => lead.toUpperCase());
  });

/** @param {string} storeName */
const storeLabel = (storeName) =>
  toTitleCase(storeName.trim().replace(/\s+warehouse$/i, ""));

/**
 * Retailer id → dataset `storeType` values. Ids match the `data-retailer`
 * chips and the `data-store-logo` templates in blocks/locations-map.liquid.
 * @type {Record<string, string[]>}
 */
const RETAILERS = {
  costco: ["COSTCO"],
  home_depot: ["HOME DEPOT"],
  lowes: ["LOWES"],
  best_buy: ["BEST BUY", "BEST BUY MOBILE"],
  kent: ["KENT"],
  london_drugs: ["LONDON DRUGS"],
  northwest_tel: ["NORTHWESTTEL"],
};

/**
 * @param {string} storeType
 * @returns {string | null}
 */
const retailerFor = (storeType) =>
  Object.keys(RETAILERS).find((id) => RETAILERS[id].includes(storeType)) ??
  null;

/**
 * Empty retailer matches every store.
 * @param {LocationData} location
 * @param {string} retailer
 */
const matchesRetailer = (location, retailer) =>
  !retailer || retailerFor(location.storeType) === retailer;

/**
 * @param {string} phone
 * @returns {HTMLAnchorElement}
 */
const createTelLink = (phone) => {
  const link = document.createElement("a");
  link.href = `tel:${phone.replace(/[^\d+]/g, "")}`;
  link.textContent = phone;
  return link;
};

class StoreLocator {
  /** @param {StoreLocatorConfig} config */
  constructor(config) {
    this.config = config;
    /** @type {google.maps.Map | null} */
    this.map = null;
    /** @type {google.maps.Marker[]} */
    this.markers = [];
    /** @type {LocationData[]} */
    this.locations = [];
    /** @type {google.maps.Marker | null} */
    this.searchMarker = null;
    this.firstLoad = true;
    /** @type {google.maps.places.Autocomplete | null} */
    this.autocomplete = null;
    /** @type {HTMLElement | null} */
    this.detailPanel = null;
    /** @type {LocationData | null} */
    this.activeLocation = null;
    /** Sidebar control that opened the detail panel; focus returns to it. */
    /** @type {HTMLElement | null} */
    this.detailOpener = null;
    /** Retailer id from RETAILERS. Empty string shows every store. */
    this.activeRetailer = "";
    /** @type {HTMLElement | null} */
    this.sidebarPrompt = null;
    /** @type {HTMLElement | null} */
    this.sidebarEmpty = null;
    /** @type {HTMLElement | null} */
    this.sidebarStatus = null;
    this.pageSize = 12;
    this.visibleLimit = 12;

    this.MAP_MARKER_PATH =
      "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";
    this.SEARCH_MARKER_PATH =
      "M2 12C2 6.48 6.48 2 12 2s10 4.48 10 10-4.48 10-10 10S2 17.52 2 12zm10 6c3.31 0 6-2.69 6-6s-2.69-6-6-6-6 2.69-6 6 2.69 6 6 6z";
  }

  async init() {
    if (!globalThis.google?.maps) {
      showStoreLocatorFallback();
      reportError(new Error("Google Maps API unavailable"), {
        tags: { op: "init-guard" },
      });
      return;
    }

    try {
      await this.#loadLocationData();
      this.#initMap();
      this.#setupFilters();
      this.#setupAutocomplete();
      this.#addAllMarkers();
      this.#setupMapListeners();
    } catch (error) {
      showStoreLocatorFallback();
      console.error("Failed to initialize store locator:", error);
      reportError(error, { tags: { op: "init" } });
    }
  }

  async #loadLocationData() {
    const response = await fetch(this.config.locationDataUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to load location data: ${response.status} ${response.statusText}`,
      );
    }

    const data = /** @type {{ locations: LocationData[] }} */ (
      await response.json()
    );

    this.locations = data.locations
      .filter((loc) => loc.status === 1)
      .map((loc, index) => ({
        ...loc,
        index,
      }));
  }

  #initMap() {
    const mapElement = document.getElementById("map");
    if (!mapElement) return;

    this.map = new google.maps.Map(mapElement, {
      center: { lat: 50, lng: -92 },
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      gestureHandling: "cooperative",
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
    });

    const styledMapType = new google.maps.StyledMapType(
      [
        {
          featureType: "administrative",
          elementType: "labels.text.fill",
          stylers: [{ color: "#444444" }],
        },
        {
          featureType: "landscape",
          elementType: "all",
          stylers: [{ color: "#f2f2f2" }],
        },
        {
          featureType: "poi",
          elementType: "all",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "road",
          elementType: "all",
          stylers: [{ saturation: -100 }, { lightness: 45 }],
        },
        {
          featureType: "road.highway",
          elementType: "all",
          stylers: [{ visibility: "simplified" }],
        },
        {
          featureType: "road.arterial",
          elementType: "labels.icon",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          elementType: "all",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "water",
          elementType: "all",
          stylers: [{ color: "#46bcec" }, { visibility: "on" }],
        },
        {
          featureType: "water",
          elementType: "geometry.fill",
          stylers: [
            { visibility: "on" },
            { color: "#0071bc" },
            { lightness: "0" },
            { gamma: "3" },
          ],
        },
      ],
      { name: "Styled Map" },
    );

    this.map.mapTypes.set("styled_map", styledMapType);
    this.map.setMapTypeId("styled_map");

    this.detailPanel = document.querySelector("[data-location-detail]");
    this.sidebarPrompt = document.querySelector("[data-sidebar-prompt]");
    this.sidebarEmpty = document.querySelector("[data-sidebar-empty]");
    this.sidebarStatus = document.querySelector("[data-sidebar-status]");

    const closeBtn = document.querySelector("[data-location-detail-close]");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.#hideLocationDetail());
    }
  }

  #setupFilters() {
    const group = document.querySelector("[data-store-filter]");
    if (!group) return;

    for (const chip of group.querySelectorAll("[data-retailer]")) {
      if (!(chip instanceof HTMLElement)) continue;
      const countEl = chip.querySelector("[data-store-count]");
      if (!countEl) continue;
      const retailer = chip.dataset.retailer ?? "";
      const count = this.locations.filter((location) =>
        matchesRetailer(location, retailer),
      ).length;
      countEl.textContent = `(${count})`;
    }

    group.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const chip = target.closest("[data-retailer]");
      if (!(chip instanceof HTMLElement) || !group.contains(chip)) return;
      this.#setFilter(chip.dataset.retailer ?? "");
    });
  }

  /**
   * Apply a retailer filter in one pass: chips, markers, detail panel,
   * search fit, sidebar. `idle` may redraw the sidebar again if the
   * viewport moved; that is cheaper than depending on it.
   * @param {string} retailer
   */
  #setFilter(retailer) {
    if (retailer === this.activeRetailer) return;
    this.activeRetailer = retailer;

    const chips = document.querySelectorAll(
      "[data-store-filter] [data-retailer]",
    );
    for (const chip of chips) {
      if (!(chip instanceof HTMLElement)) continue;
      const active = (chip.dataset.retailer ?? "") === retailer;
      chip.classList.toggle("is-active", active);
      chip.setAttribute("aria-pressed", String(active));
    }

    for (const [index, marker] of this.markers.entries()) {
      const location = this.locations[index];
      const visible = location ? this.#matchesFilter(location) : false;
      marker.setMap(visible ? this.map : null);
    }
    this.visibleLimit = this.pageSize;

    if (this.activeLocation && !this.#matchesFilter(this.activeLocation)) {
      this.#hideLocationDetail();
    }

    const searchPosition = this.searchMarker?.getPosition();
    if (searchPosition) this.#fitSearchBounds(searchPosition);

    // Choosing a retailer is explicit intent: end the initial prompt state
    // even if the map has not settled yet.
    this.firstLoad = false;
    this.#updateSidebar();
  }

  /** @param {LocationData} location */
  #matchesFilter(location) {
    return matchesRetailer(location, this.activeRetailer);
  }

  /**
   * Fit the map around the search point and the nearest store that passes
   * the retailer filter.
   * @param {google.maps.LatLng} origin
   */
  #fitSearchBounds(origin) {
    if (!this.map) return;

    const nearest = this.#sortLocationsByDistance(origin).find((location) =>
      this.#matchesFilter(location),
    );
    if (!nearest) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(nearest.lat, nearest.lng));
    bounds.extend(origin);
    this.map.fitBounds(bounds);

    const zoom = this.map.getZoom();
    if (zoom !== undefined && zoom > 13) {
      this.map.setZoom(13);
    }
  }

  #setupAutocomplete() {
    const input = /** @type {HTMLInputElement | null} */ (
      document.getElementById("pac-input")
    );
    if (!input) return;

    this.autocomplete = new google.maps.places.Autocomplete(input);
    this.autocomplete.setComponentRestrictions({ country: ["ca", "us"] });
    this.autocomplete.addListener("place_changed", () =>
      this.#handlePlaceChanged(),
    );

    this.#enableEnterKeySelection(input);
  }

  /** @param {HTMLInputElement} input */
  #enableEnterKeySelection(input) {
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        const suggestions = document.querySelectorAll(".pac-item");

        if (
          suggestions.length > 0 &&
          !document.querySelector(".pac-item-selected")
        ) {
          event.preventDefault();
          const downArrowEvent = new KeyboardEvent("keydown", {
            key: "ArrowDown",
            code: "ArrowDown",
            keyCode: 40,
            which: 40,
            bubbles: true,
          });
          input.dispatchEvent(downArrowEvent);
        }
      }
    });
  }

  #addAllMarkers() {
    for (const location of this.locations) {
      this.#addMarker(location);
    }
  }

  /** @param {LocationData} location */
  #addMarker(location) {
    const marker = new google.maps.Marker({
      position: { lat: location.lat, lng: location.lng },
      map: this.map,
      optimized: false,
      icon: {
        path: this.MAP_MARKER_PATH,
        fillColor: "#d4145a",
        fillOpacity: 1,
        strokeColor: "white",
        anchor: new google.maps.Point(12, 24),
        scale: 1.7,
      },
    });

    marker.addListener("click", () => {
      this.#showLocationDetail(location);
    });

    this.markers.push(marker);
  }

  /** @param {string} storeType */
  #getStoreLogo(storeType) {
    const retailer = retailerFor(storeType);
    if (!retailer) return null;
    const template = document.querySelector(`[data-store-logo="${retailer}"]`);
    if (
      typeof HTMLTemplateElement === "undefined" ||
      !(template instanceof HTMLTemplateElement)
    )
      return null;
    return template.content.cloneNode(true);
  }

  /** @param {LocationData} location */
  #showLocationDetail(location) {
    if (!this.detailPanel) return;
    this.activeLocation = location;

    const logoEl = this.detailPanel.querySelector("[data-detail-logo]");
    const nameEl = this.detailPanel.querySelector("[data-detail-name]");
    const addressEl = this.detailPanel.querySelector("[data-detail-address]");
    const mallEl = /** @type {HTMLElement | null} */ (
      this.detailPanel.querySelector("[data-detail-mall]")
    );
    const contactEl = this.detailPanel.querySelector("[data-detail-contact]");

    if (logoEl) {
      logoEl.replaceChildren();
      const logo = this.#getStoreLogo(location.storeType);
      if (logo) logoEl.appendChild(logo);
    }

    if (nameEl) nameEl.textContent = storeLabel(location.storeName);

    if (addressEl) {
      addressEl.textContent = `${toTitleCase(location.address.trim())}, ${toTitleCase(location.city.trim())}, ${location.state} ${location.postalCode}`;
    }

    if (mallEl) {
      if (location.mallName && location.mallName !== 0) {
        mallEl.textContent = `Mall: ${location.mallName}`;
        mallEl.hidden = false;
      } else {
        mallEl.textContent = "";
        mallEl.hidden = true;
      }
    }

    if (contactEl) {
      contactEl.replaceChildren("Phone: ", createTelLink(location.phone));
      if (location.fax) contactEl.append(` | Fax: ${location.fax}`);
    }

    const sidebar = document.getElementById("sidebar");
    if (sidebar) sidebar.hidden = true;

    this.detailPanel.classList.remove("is-visible");
    this.detailPanel.classList.add("is-active");
    this.detailPanel.offsetHeight;
    this.detailPanel.classList.add("is-visible");

    // The sidebar is now hidden; move focus off the card that opened this.
    const opener = document.activeElement;
    if (opener instanceof HTMLElement && sidebar?.contains(opener)) {
      this.detailOpener = opener;
      const closeBtn = this.detailPanel.querySelector(
        "[data-location-detail-close]",
      );
      if (closeBtn instanceof HTMLElement) closeBtn.focus();
    }
  }

  #hideLocationDetail() {
    if (!this.detailPanel) return;
    this.activeLocation = null;
    this.detailPanel.classList.remove("is-visible", "is-active");

    const sidebar = document.getElementById("sidebar");
    if (sidebar) sidebar.hidden = false;

    const opener = this.detailOpener;
    this.detailOpener = null;
    if (!opener || !this.detailPanel.contains(document.activeElement)) return;

    // The sidebar may have re-rendered while the panel was open (the map
    // zooms to the store), so fall back to the first card.
    const target = opener.isConnected
      ? opener
      : sidebar?.querySelector(".location-card__name");
    if (target instanceof HTMLElement) target.focus();
  }

  #setupMapListeners() {
    if (!this.map) return;

    this.map.addListener("idle", () => {
      this.#updateSidebar();
    });
  }

  #updateSidebar() {
    if (this.firstLoad) {
      this.firstLoad = false;
      return;
    }

    const sidebar = document.getElementById("sidebar");
    if (!sidebar) return;

    const sortOrigin =
      this.searchMarker?.getPosition() ?? this.map?.getCenter();
    if (!sortOrigin || !this.map) return;

    const inView = this.#locationsInView(sortOrigin);
    const shown = inView.slice(0, this.visibleLimit);

    sidebar.replaceChildren();

    if (shown.length === 0) {
      const message = this.activeRetailer
        ? this.sidebarEmpty
        : this.sidebarPrompt;
      if (message) {
        message.hidden = false;
        sidebar.appendChild(message);
      }
      return;
    }

    // Persistent live region: moving it keeps announcements working.
    if (this.sidebarStatus) {
      this.sidebarStatus.textContent = this.#sidebarCountLabel(
        shown.length,
        inView.length,
      );
      this.sidebarStatus.hidden = false;
      sidebar.appendChild(this.sidebarStatus);
    }

    for (const [index, location] of shown.entries()) {
      const card = this.#createSidebarCard(location, index + 1);
      if (card) sidebar.appendChild(card);
    }

    if (shown.length < inView.length) {
      const more = document.createElement("button");
      more.type = "button";
      more.className = "locator-button sidebar-more";
      more.textContent = sidebar.dataset.showMoreLabel ?? "";
      more.addEventListener("click", () => {
        const firstNew = this.visibleLimit;
        this.visibleLimit += this.pageSize;
        this.#updateSidebar();
        const names = sidebar.querySelectorAll(".location-card__name");
        const target = names[firstNew];
        if (target instanceof HTMLElement) target.focus();
      });
      sidebar.appendChild(more);
    }

    requestAnimationFrame(() => {
      for (const card of sidebar.querySelectorAll(".location-card")) {
        card.classList.add("is-visible");
      }
    });
  }

  /**
   * @param {google.maps.LatLng} sortOrigin
   */
  #locationsInView(sortOrigin) {
    const bounds = this.map?.getBounds();
    return this.#sortLocationsByDistance(sortOrigin).filter((location) => {
      if (!this.#matchesFilter(location)) return false;
      if (!bounds) return true;
      return bounds.contains(
        new google.maps.LatLng(location.lat, location.lng),
      );
    });
  }

  /**
   * @param {number} shown
   * @param {number} total
   */
  #sidebarCountLabel(shown, total) {
    const sidebar = document.getElementById("sidebar");
    const template = sidebar?.dataset.countLabel ?? "";
    return template
      .replace("__SHOWN__", String(shown))
      .replace("__TOTAL__", String(total));
  }

  /**
   * @param {LocationData} location
   * @param {number} count
   */
  #createSidebarCard(location, count) {
    const template = document.getElementById("location-item-template");
    if (
      !template ||
      typeof HTMLTemplateElement === "undefined" ||
      !(template instanceof HTMLTemplateElement)
    )
      return null;

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );

    const card = /** @type {HTMLElement} */ (
      fragment.querySelector(".location-card")
    );
    if (!card) return null;

    const logoEl = fragment.querySelector("[data-logo]");
    const numberEl = fragment.querySelector(".location-card__num");
    const nameEl = fragment.querySelector("[data-name]");
    const addressEl = fragment.querySelector("[data-address]");
    const placeEl = fragment.querySelector("[data-place]");
    const phoneEl = fragment.querySelector("[data-phone]");

    if (logoEl) {
      const logo = this.#getStoreLogo(location.storeType);
      const retailerName = toTitleCase(location.storeType);
      if (logo) {
        const label = document.createElement("span");
        label.className = "visually-hidden";
        label.textContent = retailerName;
        logoEl.append(logo, label);
      } else {
        logoEl.textContent = retailerName;
      }
    }
    if (numberEl) numberEl.textContent = String(count);
    if (nameEl) nameEl.textContent = storeLabel(location.storeName);
    if (addressEl) addressEl.textContent = toTitleCase(location.address.trim());
    if (placeEl) {
      placeEl.textContent = `${toTitleCase(location.city.trim())}, ${location.state} ${location.postalCode}`;
    }
    if (phoneEl && location.phone) {
      const link = createTelLink(location.phone);
      link.addEventListener("click", (event) => event.stopPropagation());
      phoneEl.appendChild(link);
    }

    card.addEventListener("click", () => {
      this.gotoMarker(location.index);
    });

    return card;
  }

  /** @param {google.maps.LatLng} point */
  #sortLocationsByDistance(point) {
    const sorted = this.locations.map((loc) => ({
      ...loc,
      distance: google.maps.geometry.spherical.computeDistanceBetween(
        point,
        new google.maps.LatLng(loc.lat, loc.lng),
      ),
    }));

    return sorted.sort((a, b) => a.distance - b.distance);
  }

  #handlePlaceChanged() {
    if (!this.autocomplete || !this.map) return;

    const place = this.autocomplete.getPlace();

    if (!place.geometry?.location) {
      window.alert(`No details available for input: '${place.name}'`);
      return;
    }

    if (this.searchMarker) {
      this.searchMarker.setMap(null);
    }

    this.visibleLimit = this.pageSize;
    this.searchMarker = new google.maps.Marker({
      map: this.map,
      position: place.geometry.location,
      icon: {
        path: this.SEARCH_MARKER_PATH,
        fillColor: "#0071bc",
        fillOpacity: 1,
        strokeColor: "white",
        anchor: new google.maps.Point(12, 24),
        scale: 1.3,
      },
    });

    this.#fitSearchBounds(place.geometry.location);
  }

  /** @param {number} index */
  gotoMarker(index) {
    const marker = this.markers[index];
    const location = this.locations[index];
    if (!marker || !location || !this.map) return;

    this.#showLocationDetail(location);
    this.map.setCenter(
      /** @type {google.maps.LatLng} */ (marker.getPosition()),
    );
    this.map.setZoom(13);

    requestAnimationFrame(() => {
      document
        .getElementById("map")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}

/** @param {HTMLElement} mapElement */
const loadStoreLocator = (mapElement) => {
  const { apiKey, locationsUrl } = mapElement.dataset;
  if (!apiKey || !locationsUrl) {
    showStoreLocatorFallback();
    reportError(
      new Error(
        "Store locator missing required data-api-key or data-locations-url",
      ),
      { tags: { op: "config" } },
    );
    return;
  }

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
  script.dataset.googleMapsApi = "";

  script.addEventListener("load", async () => {
    const locator = new StoreLocator(
      /** @type {StoreLocatorConfig} */ ({
        locationDataUrl: locationsUrl,
        apiKey,
      }),
    );
    await locator.init();
  });

  script.addEventListener("error", () => {
    showStoreLocatorFallback();
    reportError(new Error("Google Maps API script failed to load"), {
      tags: { op: "script-load" },
    });
  });

  document.head.appendChild(script);
};

const mapElement = document.getElementById("map");

if (mapElement && !document.querySelector("[data-google-maps-api]")) {
  loadStoreLocator(mapElement);
}
